import { AD } from '@gumtree/shared/src/model/generic.model';
import { redirectToUrl } from '@gumtree/ui-library/src/utils/browser-service';
import { RevealedNumber } from '@gumtree/shared/src/model/seller-contact-details.model';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { VipThunk } from '.';
import fetchReveal from '../get-data/fetch/fetch-reveal';

export interface State {
    contactName: string;
    replyPhone: string;
    userLoggedIn: boolean;
    revealSellerTelephoneNumberToken: string;
    revealedNumber: RevealedNumber;
    isLoginRequiredToContactForVipCategory: boolean;
    sellerUserId: number;
    showRevealNumber: boolean;
    revealNumberUnmasked: string;
    replySmsEnabled: boolean;
    revealPhoneCallbackUrl: string;
    replyEmail?: string;
    replyLink?: string;
    websiteUrl?: string;
    websiteName?: string;
    thirdPartySellerId?: string;
}

const initialState: State = {
    contactName: '',
    replyPhone: '',
    userLoggedIn: false,
    revealSellerTelephoneNumberToken: '',
    revealedNumber: {},
    isLoginRequiredToContactForVipCategory: true,
    sellerUserId: -1,
    showRevealNumber: false,
    revealNumberUnmasked: '',
    replySmsEnabled: false,
    revealPhoneCallbackUrl: '',
    replyEmail: '',
    websiteUrl: undefined,
    websiteName: undefined,
};

export const UPDATE_CONTACT_DETAILS = 'UPDATE_CONTACT_DETAILS';
export const updateContactDetails: AD<typeof UPDATE_CONTACT_DETAILS, Partial<State>> = (
    payload
) => ({
    type: UPDATE_CONTACT_DETAILS,
    payload,
});

export const REVEAL_NUMBER = 'REVEAL_NUMBER';
export const revealNumber: AD<
    typeof REVEAL_NUMBER,
    { data: string; expired: boolean; valid: boolean }
> = (payload) => ({
    type: REVEAL_NUMBER,
    payload,
});

export const revealNumberThunk: VipThunk<{
    advertId: number;
    revealSellerTelephoneNumberToken: string;
    shouldPhoneRevealBeginFunc: () => void;
    redirectToApp: string;
    isFromMotorsFeed: boolean;
    skipLoginToReveal?: boolean;
    redirectToAppCompleteFunc?: () => void;
}> =
    ({
        advertId,
        revealSellerTelephoneNumberToken,
        shouldPhoneRevealBeginFunc,
        redirectToApp,
        isFromMotorsFeed,
        skipLoginToReveal,
        redirectToAppCompleteFunc,
    }) =>
    async (dispatch) => {
        const response = await fetchReveal({
            advertId,
            revealSellerTelephoneNumberToken,
            redirectToApp,
            skipLoginToReveal,
        });

        if (response) {
            if (response.status === 401) {
                const loc = response.headers.get('Location');
                shouldPhoneRevealBeginFunc();
                loc && redirectToUrl(loc);
            } else if (response.status === 200) {
                const { data, expired, valid } = await response.json();
                dispatch(revealNumber({ data, expired, valid }));
                if (redirectToApp) {
                    // tracking event for telephone or sms event
                    const eventName =
                        redirectToApp === 'tel'
                            ? PermutiveEventNames.R2S_PHONE_BEGIN
                            : PermutiveEventNames.R2S_SMS_BEGIN;
                    trackPermutiveCustomEvent(window, eventName, {});

                    trackGA4Event<GA4.ContactSellerClickEvent>({
                        event: 'contact_a_seller',
                        contactASellerMethod:
                            redirectToApp === 'tel' ? 'click phone number' : 'click sms',
                        formValidation: 'success',
                        listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                    });

                    redirectToUrl(`${redirectToApp}:${data}`);

                    redirectToAppCompleteFunc?.();
                } else {
                    // tracking for revealing the phone number
                    const eventName = 'R2SPhoneRevealSuccess';
                    trackV2(eventName);
                    trackPermutiveCustomEvent(
                        window,
                        PermutiveEventNames.R2S_PHONE_REVEAL_SUCCESS,
                        {}
                    );

                    trackGA4Event<GA4.ContactSellerClickEvent>({
                        event: 'contact_a_seller',
                        contactASellerMethod: 'click to reveal phone number',
                        formValidation: 'success',
                        listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                    });
                }
            }
        }
    };

type Action = ReturnType<typeof updateContactDetails> | ReturnType<typeof revealNumber>;

export default (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case UPDATE_CONTACT_DETAILS: {
            return {
                ...state,
                ...action.payload,
            };
        }
        case REVEAL_NUMBER: {
            return {
                ...state,
                revealedNumber: {
                    ...action.payload,
                },
            };
        }
        default:
            return state;
    }
};
